import { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useJamesThread, useJamesStream, useJamesMessages } from '../../hooks/useJames';
import { JamesChatHeader, type JamesDockMode } from './JamesChatHeader';
import { JamesMessageList } from './JamesMessageList';
import { JamesInput } from './JamesInput';
import { JamesEmptyState } from './JamesEmptyState';
import { JamesClarifyingQuestion } from './JamesClarifyingQuestion';

interface JamesChatProps {
  threadId?: string | null;
  onThreadChange?: (threadId: string | null) => void;
  onClose?: () => void;
  showClose?: boolean;
  compact?: boolean;
  dockMode?: JamesDockMode;
  onDockModeChange?: (mode: JamesDockMode) => void;
  /** Pre-fills the input once on mount (e.g. a draft typed into the Home "Ask James" box). */
  initialDraft?: string;
}

export function JamesChat({
  threadId: propThreadId,
  onThreadChange,
  onClose,
  showClose = true,
  compact = false,
  dockMode,
  onDockModeChange,
  initialDraft,
}: JamesChatProps) {
  const [localThreadId, setLocalThreadId] = useState<string | null>(propThreadId ?? null);
  const threadId = propThreadId ?? localThreadId;

  const { data: threadData, isLoading: isLoadingThread, isError: isThreadError, refetch: refetchThread } = useJamesThread(threadId);
  const { streamingState, sendMessage, quickChat, stop, reset } = useJamesStream();

  const thread = threadData?.thread;
  const messages = useJamesMessages(thread?.messages, streamingState);

  useEffect(() => {
    if (propThreadId !== undefined) {
      setLocalThreadId(propThreadId);
    }
  }, [propThreadId]);

  useEffect(() => {
    if (streamingState.clarifyingQuestion) return;

    if (!streamingState.isStreaming && (streamingState.currentText || streamingState.pendingUserMessage)) {
      let lastAssistantContent: string | undefined;
      let lastUserContent: string | undefined;
      if (thread?.messages) {
        for (let i = thread.messages.length - 1; i >= 0; i--) {
          const m = thread.messages[i];
          if (!lastAssistantContent && m.role === 'assistant') lastAssistantContent = m.content;
          if (!lastUserContent && m.role === 'user') lastUserContent = m.content;
          if (lastAssistantContent && lastUserContent) break;
        }
      }

      const assistantSaved = !streamingState.currentText ||
        lastAssistantContent === streamingState.currentText;

      const userSaved = !streamingState.pendingUserMessage ||
        lastUserContent === streamingState.pendingUserMessage;

      if (assistantSaved && userSaved) {
        reset();
      }
    }
  }, [thread?.messages, streamingState.currentText, streamingState.pendingUserMessage, streamingState.isStreaming, streamingState.clarifyingQuestion, reset]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && streamingState.isStreaming) {
        stop();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [streamingState.isStreaming, stop]);

  const handleSend = useCallback(
    async (content: string) => {
      if (threadId) {
        await sendMessage(threadId, content);
      } else {
        const newThreadId = await quickChat(content);
        if (newThreadId) {
          setLocalThreadId(newThreadId);
          onThreadChange?.(newThreadId);
        }
      }
    },
    [threadId, sendMessage, quickChat, onThreadChange]
  );

  const handleNewThread = useCallback(() => {
    reset();
    setLocalThreadId(null);
    onThreadChange?.(null);
  }, [reset, onThreadChange]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      handleSend(suggestion);
    },
    [handleSend]
  );

  const hasMessages = messages.length > 0;
  const isDisabled = streamingState.isStreaming || isLoadingThread;
  const showHeader = compact || hasMessages || (showClose && !!onClose);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-transparent">
      {showHeader && (
        <JamesChatHeader
          title={thread?.title}
          onNewThread={hasMessages ? handleNewThread : undefined}
          onClose={onClose}
          showClose={showClose}
          dockMode={dockMode}
          onDockModeChange={onDockModeChange}
        />
      )}

      {isThreadError && !streamingState.isStreaming ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto px-6">
          <div className="card max-w-md px-8 py-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-error-light text-error-dark">
              <AlertCircle className="h-6 w-6" aria-hidden />
            </div>
            <p className="mt-4 text-body-md font-medium text-text-primary">Failed to load this conversation.</p>
            <p className="mt-1 text-body-sm text-text-secondary">
              The thread could not be opened right now. Retry and we’ll pull it back in.
            </p>
            <button
              type="button"
              onClick={() => refetchThread()}
              className="btn btn-primary mt-5 h-9 px-4 text-body-sm"
            >
              Retry
            </button>
          </div>
        </div>
      ) : hasMessages ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <JamesMessageList messages={messages} />
          {streamingState.clarifyingQuestion && (
            <JamesClarifyingQuestion
              question={streamingState.clarifyingQuestion.question}
              options={streamingState.clarifyingQuestion.options}
              onSelect={handleSend}
            />
          )}
        </div>
      ) : (
        <JamesEmptyState onSuggestionClick={handleSuggestionClick} compact={compact} />
      )}

      {streamingState.error && (
        <div className="px-4 pb-3 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl rounded-xl border border-error/20 bg-error-light px-4 py-3">
            <p className="text-body-xs font-medium text-error-dark">{streamingState.error}</p>
          </div>
        </div>
      )}

      <JamesInput
        onSend={handleSend}
        onStop={stop}
        isStreaming={streamingState.isStreaming}
        disabled={isDisabled}
        placeholder={streamingState.isStreaming ? 'James is responding...' : 'Ask James...'}
        initialDraft={initialDraft}
      />
    </div>
  );
}
